import React, { useMemo, useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react'
import { Button } from 'devextreme-react/button'
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import { BaseForm, TextInput, IxTimeRange } from 'components'
import { IxObjectRangeLookup } from 'components/forms/lookups/object-range-lookup'
import { Field } from 'formik'
import { NumberInput, RadioInput, CheckBoxInput } from 'components/forms'
import { keyboardControl } from 'helpers'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { IxLookupProvider } from 'contexts/lookup-context'
import { RootState } from 'slices'
import { setFiltersData,setActiveDivRef } from 'slices/clearing-filters-slice'
import useResizeObserver from '@react-hook/resize-observer'
import { useIxFocusElement } from 'hooks'

import './clearing-filters.scss'

const AutoUpdateForm = ({ values, itemsData, divRef, lastFocusedElement, refFilter }: any) => {
    const dispatch = useDispatch()
    const activeDivRef = useSelector((state: RootState) => state.clearingFiltersSlice.activeDivRef)

    useEffect(() => {
        dispatch(setFiltersData(
            {
                showExtendedBalance: values.showExtendedBalance,
                directLoad: values.directLoad,
                typeSumm: values.typeSumm,
                objectRangeSupplier: values.SUPPLIER,
                objectRangeUser: values.USER,
                objectRangeTeam: values.TEAM,
                personRange: values.personRange,
                personBalance: {from: values.personBalanceFrom, to: values.personBalanceTo},
                bookingAmount: {from: values.bookingAmountFrom, to: values.bookingAmountTo},
                KKL: values.KKL,
                timeRange: values.timeRange,
                dateFrom: values.dateFrom,
                dateTo: values.dateTo
            }
            ))

    },[values, dispatch])
  
    useIxFocusElement(values, itemsData, lastFocusedElement, activeDivRef, refFilter || divRef)

    return null
}

export const ClearingFilters = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { filtersData } = useSelector((state: RootState) => state.clearingFiltersSlice)
    const { objectRange, personRange, personBalance, bookingAmount, objectRangeSupplier,
       KKL, typeSumm, showExtendedBalance, objectRangeUser, objectRangeTeam, directLoad, timeRange, dateFrom, dateTo, objectRangeValue } = filtersData

    const [responsive, setResponsive] = useState<boolean>(false)

    const setFieldsData = useCallback(() => {
        const data = {personRange, personBalance, bookingAmount, timeRange, dateFrom, dateTo,  KKL, typeSumm, showExtendedBalance, objectRangeValue}
        return data
    },[personRange, personBalance, bookingAmount, timeRange, dateFrom, dateTo, KKL, typeSumm, showExtendedBalance, objectRangeValue])


    useEffect(() => {
        directLoad && setFieldsData()
    },[objectRangeValue, personRange, personBalance, bookingAmount, timeRange, dateFrom, dateTo, KKL, typeSumm, showExtendedBalance, setFieldsData, directLoad])


    const ref = useRef(null)

    const itemsData = useMemo(() => ([
        {name: 'objectRange', index: 1, type: 'field'},
        {name: 'personRange', index: 0, type: 'field'},
        {name: 'personBalanceFrom', index: 1, type: 'field'},
        {name: 'personBalanceTo', index: 1, type: 'field'},
        {name: 'bookingAmountFrom', index: 1, type: 'field'},
        {name: 'bookingAmountTo', index: 1, type: 'field'},
        {name: 'timeRange', index: 1, type: 'field'},
        {name: 'dateFrom', index: 1, type: 'field'},
        {name: 'dateTo', index: 1, type: 'field'},
        {name: 'KKL', index: 0, type: 'field'},
        {name: 'typeSumm', index: 0, type: 'field'},
        {name: 'showExtendedBalance', index: 0, type: 'field'},
        {name: 'directLoad', index: 0, type: 'field'},

        { name: 'Load', index: 0, type: 'button' }
    ]),[])

    const lastFocusedElement = useRef<any>(null)
    const onKeyboardControl = keyboardControl(itemsData, t, ref, lastFocusedElement)

    useEffect(() => {
        const filterElement: any = (ref.current as any).closest('.dx-drawer-panel-content') || (ref.current as any).closest('.dx-drawer-content')

        filterElement?.addEventListener('keydown', onKeyboardControl)

        return () => {
            filterElement?.removeEventListener('keydown', onKeyboardControl)
        }
    },[onKeyboardControl])

    useEffect(() => {
        const filterElement: any = (ref.current as any).closest('.dx-drawer-panel-content') || (ref.current as any).closest('.dx-drawer-content')

        const setPrevFocusedElement = (e: any) => {
            const filterButton = e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]')
            dispatch(setActiveDivRef(e.target.closest('.dx-drawer-content') || e.target.closest('.dx-drawer-panel-content')))
                        
            if (e.target.tagName === 'DIV' && !e.target.className.includes('dropdowneditor') && !e.target.className.includes('button') && !e.target.closest('.dx-radiobutton') && filterButton) {
                setTimeout(() => {
                    e.target.closest('.dx-drawer-panel-content').querySelector('[aria-label="filter"]').focus()
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').classList.remove('dx-state-focused')
                }, 0)
            }

            if (e.target.tagName === 'INPUT' || e.target.tagName === 'I' || e.target.className.includes('dropdowneditor')) {
                lastFocusedElement.current = e.target
                filterElement?.querySelector('.dx-state-focused.dx-radiobutton')?.classList.remove('dx-state-focused')
                filterElement?.querySelector('.dx-checkbox.dx-state-focused')?.classList.remove('dx-state-focused')
            }
        }

        filterElement?.addEventListener('mousedown', setPrevFocusedElement)

        return () => {
            filterElement?.removeEventListener('mousedown', setPrevFocusedElement)
        }
    }, [dispatch])

    const formik = useMemo(() => ({
        initialValues: {
            objectRange: objectRange,
            personRange: personRange,
            personBalanceFrom: personBalance.from,
            personBalanceTo: personBalance.to,
            bookingAmountFrom: bookingAmount.from,
            bookingAmountTo: bookingAmount.to,
            KKL: KKL,
            USER: objectRangeUser,
            TEAM: objectRangeTeam,
            SUPPLIER: objectRangeSupplier,
            typeSumm: typeSumm,
            showExtendedBalance: showExtendedBalance,
            directLoad: directLoad,
            timeRange: timeRange,
            dateFrom: dateFrom,
            dateTo: dateTo,

        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },

    }),[personRange, personBalance, timeRange, dateFrom, dateTo, KKL, directLoad,
        bookingAmount, objectRangeTeam, objectRangeSupplier, objectRange, objectRangeUser, typeSumm, showExtendedBalance])

    useLayoutEffect(() => {
        const divRef: any = ref.current

        if (divRef) {
            const { x } = divRef.getBoundingClientRect()
            setResponsive(x < 705)
        }

    },[])

    const onResponsive = useCallback((e: any) => {
        setResponsive(e.contentRect.width < 705)
    },[])

    useResizeObserver(ref, onResponsive)

    return (
        <IxLookupProvider>
            {
                responsive
                   ? <div
                        className={'clearing-filters clearing-filters-responce'}
                        ref={ref}
                    >
                        <BaseForm {...formik} >
                            {({ values }: any) => (
                                <>
                                    <AutoUpdateForm values={values} lastFocusedElement={lastFocusedElement} divRef={ref} itemsData={itemsData}/>
                                    <ResponsiveBox>
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />
                                        <Row />

                                        <Col/>
                                        <Col/>
                                        <Col/>
                                        <Col/>
                                        <Col/>
                                        <Col />


                                        <Item>
                                            <Location row={0} col={0} colspan={6}/>
                                            <Field
                                                as={IxObjectRangeLookup}
                                                name='objectRange'
                                                label='Object Range'
                                                active={true}
                                                setFiltersDataToStore={setFiltersData}
                                                filtersDataFromStore={filtersData}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={1} col={0} colspan={6}/>
                                            <Field
                                                as={TextInput}
                                                name='personRange'
                                                label='Person range'
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={2} col={0} colspan={3}/>
                                            <Field
                                                as={NumberInput}
                                                name='personBalanceFrom'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                                label='Person balance'
                                            />
                                        </Item>
                                        <Item>
                                            <Location row={2} col={3} colspan={3}/>
                                            <Field
                                                as={NumberInput}
                                                name='personBalanceTo'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={3} col={0} colspan={3}/>
                                            <Field
                                                as={NumberInput}
                                                name='bookingAmountFrom'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                                label='Booking amount'
                                            />
                                        </Item>
                                        <Item>
                                            <Location row={3} col={3} colspan={3}/>
                                            <Field
                                                as={NumberInput}
                                                name='bookingAmountTo'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={6} col={0} colspan={6}/>
                                            <Field
                                                as={IxTimeRange}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={7} col={0} colspan={6}/>
                                            <Field
                                                as={TextInput}
                                                name='KKL'
                                                label='KKL'
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={8} col={0} colspan={6}/>
                                            <div className='options'>
                                                <p>Options</p>
                                                <Field
                                                    as={RadioInput}
                                                    opts={[{name: 'Overall sum', value: 'Overall sum'},{name: 'Sum per month', value: 'Sum per month'}]}
                                                    name='typeSumm'
                                                    layout={'vertical'}
                                                />
                                                <Field
                                                    as={CheckBoxInput}
                                                    text='Show extended balance'
                                                    name='showExtendedBalance'
                                                />
                                                <Field
                                                    as={CheckBoxInput}
                                                    text='Direct load'
                                                    name='directLoad'
                                                />
                                                <Button disabled={ directLoad } text='Load' onClick={setFieldsData}/>
                                            </div>
                                        </Item>
                                    </ResponsiveBox>
                                </>
                            )}
                        </BaseForm>
                       {/* {
                        !directLoad && <Button text='Load' onClick={setFieldsData}/>
                       } */}
                    </div>
                        : <div className={'clearing-filters'} ref={ref}>
                        <BaseForm {...formik} >
                            {({ values }: any) => (
                                <>
                                    <AutoUpdateForm values={values} lastFocusedElement={lastFocusedElement} itemsData={itemsData} refFilter={ref}/>
                                    <ResponsiveBox>
                                        <Row />
                                        <Row />
                                        <Row />


                                        <Col/>
                                        <Col/>
                                        <Col/>
                                        <Col/>
                                        <Col  ratio={4}/>
                                        <Col />


                                        <Item>
                                            <Location row={0} col={0} colspan={4}/>
                                            <Field
                                                as={IxObjectRangeLookup}
                                                name='objectRange'
                                                label='Object Range'
                                                active={true}
                                                setFiltersDataToStore={setFiltersData}
                                                filtersDataFromStore={filtersData}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={1} col={0} colspan={4}/>
                                            <Field
                                                as={TextInput}
                                                name='personRange'
                                                label='Person range'
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={2} col={0} />
                                            <Field
                                                as={NumberInput}
                                                name='personBalanceFrom'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                                label='Person balance'
                                            />
                                        </Item>
                                        <Item>
                                            <Location row={2} col={1} />
                                            <Field
                                                as={NumberInput}
                                                name='personBalanceTo'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={2} col={2} />
                                            <Field
                                                as={NumberInput}
                                                name='bookingAmountFrom'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                                label='Booking amount'
                                            />
                                        </Item>
                                        <Item>
                                            <Location row={2} col={3} />
                                            <Field
                                                as={NumberInput}
                                                name='bookingAmountTo'
                                                format={{ type: 'fixedPoint', precision: 2 }}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={0} col={4} rowspan={2} />
                                            <Field
                                                as={IxTimeRange}
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={2} col={4}/>
                                            <Field
                                                as={TextInput}
                                                name='KKL'
                                                label='KKL'
                                            />
                                        </Item>

                                        <Item>
                                            <Location row={0} col={5} rowspan={2}/>
                                            <div className='options'>
                                                <p>Options</p>
                                                <Field
                                                    as={RadioInput}
                                                    opts={[{name: 'Overall sum', value: 'Overall sum'},{name: 'Sum per month', value: 'Sum per month'}]}
                                                    name='typeSumm'
                                                    layout={'vertical'}
                                                />
                                                <Field
                                                    as={CheckBoxInput}
                                                    text='Show extended balance'
                                                    name='showExtendedBalance'
                                                />
                                                <Field
                                                    as={CheckBoxInput}
                                                    text='Direct load'
                                                    name='directLoad'
                                                />
                                                <Button disabled={ directLoad } text='Load' onClick={setFieldsData}/>
                                            </div>
                                        </Item>
                                    </ResponsiveBox>
                                </>
                            )}
                        </BaseForm>
                        {/* {
                            !directLoad && <Button disabled={ directLoad } text='Load' onClick={setFieldsData}/>
                        } */}
                    </div>
            }
        </IxLookupProvider>
    )

}
